import { type GetComplianceCaseQuery } from "~/graphql/supergraphTypes";
import {
  EXTERNAL_ESCALATION_TYPE_LABEL,
  INTERNAL_ESCALATION_TYPE_LABEL,
} from "../../../utils/constants";
import { getHeaderTitle } from "../../CaseDetailsHeader/utils";
import { type ImpactedGPRow } from "../IssueGrid/ImpactedGPGrid/types";

export type ParentRelationship = {
  parentProfileId: number;
  relationshipType: string;
};

export type EscalationPreviewRow = {
  /** Tree path: `[arenaId]` for a client row, `[arenaId, onboardingCaseId]` for a position row. */
  path: string[];
  subject: string;
  position?: string;
  profileRole?: string;
  escalationType?: string;
  isOriginatingCase?: boolean;
};

type ComplianceCase = NonNullable<GetComplianceCaseQuery["complianceCase"]>;

const profileRoleLabel = (relationshipType?: string | null): string => {
  if (!relationshipType) return "--";
  const [first, ...rest] = relationshipType.split("_");
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(" ");
};

/** The escalated profile is either the root (investing) profile itself or a relation of it. */
const roleForRoot = (
  relationships: readonly ParentRelationship[] | null | undefined,
  rootProfileId: number | null | undefined,
  escalatedProfileId: number | null | undefined,
): string => {
  if (rootProfileId != null && rootProfileId === escalatedProfileId) return "Investing profile";
  return profileRoleLabel(
    relationships?.find((relationship) => relationship.parentProfileId === rootProfileId)
      ?.relationshipType,
  );
};

/** Internal escalations spawn one case for the source case's own subject, as the drawer header names it. */
export const internalPreviewRow = (
  complianceCase: ComplianceCase,
  escalatedProfileId: number | null | undefined,
  relationships: readonly ParentRelationship[] | null | undefined,
): EscalationPreviewRow => {
  const subject = getHeaderTitle(complianceCase, true);
  return {
    path: [subject],
    subject,
    profileRole: roleForRoot(
      relationships,
      complianceCase.investingProfile?.id,
      escalatedProfileId,
    ),
    escalationType: INTERNAL_ESCALATION_TYPE_LABEL,
  };
};

/** External escalations spawn one case per client; positions are listed under each client. */
export const externalPreviewRows = (
  impactedRows: readonly ImpactedGPRow[],
  relationships: readonly ParentRelationship[] | null | undefined,
  currentCaseId: number,
  escalatedProfileId: number | null | undefined,
): EscalationPreviewRow[] =>
  impactedRows.flatMap((row) => {
    const clientPath = [String(row.arenaId)];
    const positions = row.details.map((detail) => ({
      path: [...clientPath, String(detail.positionName.id)],
      subject: row.GP,
      position: detail.positionName.name,
      profileRole: roleForRoot(relationships, detail.investingProfileId, escalatedProfileId),
      isOriginatingCase: detail.positionName.id === currentCaseId,
    }));
    return [
      {
        path: clientPath,
        subject: row.GP,
        escalationType: EXTERNAL_ESCALATION_TYPE_LABEL,
        isOriginatingCase:
          row.case?.id === currentCaseId &&
          !positions.some((position) => position.isOriginatingCase),
      },
      ...positions,
    ];
  });

export const createButtonLabel = (count: number): string =>
  count > 0 ? `Create ${count} escalation${count === 1 ? "" : "s"}` : "Create escalation";
